import { Box, Input, Select } from '@nerdwallet/currency';
import MarketplaceStarRating from '@nerdwallet/react-marketplace-star-rating';
import { useMemo } from 'react';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { useIsHydrated } from '~/app/lib/use-is-hydrated';
import SegmentImpression from '~/app/components/segment-impression';
import {
  MIN_RATING,
  STAR_RATING_SOURCE_TYPES,
  useDynamicStarRating,
} from './use-dynamic-star-rating-form';
import { useDynamicAnalytics } from '../../segment/use-dynamic-analytics';
import {
  SegmentTrackingForm,
  SegmentTrackingProps,
} from '../../segment/segment-tracking-form';

type DynamicStarRatingProps = {
  starRating?: number;
  starRatingLayout?: 'stacked' | 'big-number' | 'numeric';
  marketplaceOfferId?: string;
  starRatingSource?: string;
  segment?: SegmentTrackingProps;
};

const layoutOptions = [
  { label: 'Default', value: undefined },
  { label: 'Stacked', value: 'stacked' },
  { label: 'Big Number', value: 'big-number' },
  { label: 'Numeric', value: 'numeric' },
];

export const DynamicStarRating = ({
  starRating = MIN_RATING,
  starRatingLayout = undefined, // Default to undefined to use the default layout
  marketplaceOfferId = '',
  starRatingSource = STAR_RATING_SOURCE_TYPES.CUSTOM,
  segment,
}: DynamicStarRatingProps) => {
  const isHydrated = useIsHydrated();

  const { marketplaceOffers } = useDynamicGrid();
  const isApplyNowButtonType =
    starRatingSource === STAR_RATING_SOURCE_TYPES.MARKETPLACE_OFFER;

  const selectedMarketplaceOffer = useMemo(() => {
    return marketplaceOffers?.find(
      (offer: any) => offer?.id === marketplaceOfferId
    );
  }, [marketplaceOffers, marketplaceOfferId]);

  const starRatingValue =
    starRatingSource === STAR_RATING_SOURCE_TYPES.MARKETPLACE_OFFER
      ? Number(selectedMarketplaceOffer?.starRating || MIN_RATING)
      : Number(starRating || MIN_RATING);

  const { impressionViewProps, handleTrackInteraction } = useDynamicAnalytics({
    isApplyNowButtonType,
    selectedMarketplaceOffer,
    enableInteractionTracking: segment?.enableInteractionTracking || false,
    entityName: segment?.entityName || '',
    impressionSection: segment?.impressionSection || '',
    impressionPosition: segment?.impressionPosition || '',
    extraProps: {
      sub_location: 'star_rating',
    },
  });

  if (!isHydrated) {
    return null;
  }

  return (
    <SegmentImpression
      eventName="Star Rating Viewed"
      eventProps={impressionViewProps}
    >
      <Box onClick={handleTrackInteraction}>
        <MarketplaceStarRating
          ratingTextHref={selectedMarketplaceOffer?.reviewLink}
          starRating={starRatingValue}
          layout={starRatingLayout}
        />
      </Box>
    </SegmentImpression>
  );
};

export const DynamicStarRatingForm = () => {
  const {
    rating,
    layout,
    marketplaceOfferId,
    starRatingSource,
    handleChangeRating,
    handleChangeLayout,
    handleMarketplaceOfferChange,
    starRatingSourceOptions,
    marketplaceOfferOptions,
    handleStarRatingSourceChange,
  } = useDynamicStarRating();

  return (
    <Box paddingY={2}>
      <Box display="flex">
        <Box paddingY={2} paddingRight={2}>
          <Input
            label="Rating"
            fullWidth
            value={rating}
            onChange={handleChangeRating}
            type="number"
            inputMode="decimal"
            disabled={
              starRatingSource === STAR_RATING_SOURCE_TYPES.MARKETPLACE_OFFER
            }
          />
        </Box>
        <Box paddingY={2}>
          <Select
            label="Layout"
            value={layout}
            onChange={handleChangeLayout}
            options={layoutOptions}
          />
        </Box>
      </Box>
      <Box paddingY={2}>
        <Select
          fullWidth
          value={starRatingSource}
          onChange={handleStarRatingSourceChange}
          label="Star Rating Source"
          options={starRatingSourceOptions()}
        />
      </Box>
      {starRatingSource === STAR_RATING_SOURCE_TYPES.MARKETPLACE_OFFER && (
        <Box paddingY={2}>
          <Select
            fullWidth
            value={marketplaceOfferId}
            onChange={handleMarketplaceOfferChange}
            label="Marketplace Offer"
            options={marketplaceOfferOptions()}
          />
        </Box>
      )}
      <SegmentTrackingForm
        isMarketplaceOfferButtonType={
          starRatingSource === STAR_RATING_SOURCE_TYPES.MARKETPLACE_OFFER
        }
        isCustomUrlButtonType={
          starRatingSource === STAR_RATING_SOURCE_TYPES.CUSTOM
        }
      />
    </Box>
  );
};
